import {
	INCOME_VERIFICATION_TYPES,
	LOAN_PURPOSE_TYPES,
	YES_NO,
	CITIZENSHIP,
	BANKRUPTCY_TYPE,
	HOUSING_EVENT_TYPE,
	HOUSE_EVENT_SEASONING,
	BANKRUPTCY_OUTCOME,
	BANKRUPTCY_SEASONING,
	PROPERTY_TYPE,
	OCCUPANCY,
	TERMS,
	AMORTIZATION_TYPES,
	ARM_FIXED_TERMS,
	DESIRED_LOCK_PERIOD,
	PRODUCT_TYPES,
	LOAN_TYPES,
	LIEN_TYPES,
	ESCROW_WAIVER,
	LENDER_VS_BORROW,
	PROPERTY_UNITS,
	PREPAYMENT_PENALTY_OPTIONAL
} from "../../utils/quickPricerConstants";
import { calculateLtvCltv } from "../../utils/ltvCltvCalculator";

export const QUICK_PRICER_FORM_CONFIG = {
	formName: "quick-pricer",

	sections: [
		{
			name: "loanInformation",
			title: "Loan Information",
			fields: [
				{
					name: "incomeVerificationType",
					label: "Income Verification Type",
					component: "select",
					options: INCOME_VERIFICATION_TYPES,
					required: true,
				},
				{
					name: "debtToIncomeRatio",
					label: "Debt-to-Income Ratio (DTI)",
					component: "masked",
					maskType: "percentage",
					placeholder: "0.00",

					required: (formState) => {
						const type = formState.incomeVerificationType;
						return !(type === "InvestorDscr" || type === "InvestorNoRatio");
					},

					disabled: (formState) => {
						const type = formState.incomeVerificationType;
						return type === "InvestorDscr" || type === "InvestorNoRatio";
					},
				},
				{
					name: "debtServiceCoverageRatio",
					label: "Debt Service Coverage Ratio (DSCR)",
					component: "masked",
					maskType: "ratio",
					placeholder: "0.00",

					required: (formState) => {
						const type = formState.incomeVerificationType;
						return type === "InvestorDscr" || type === "InvestorNoRatio";
					},

					disabled: (formState) => {
						const type = formState.incomeVerificationType;
						return !(type === "InvestorDscr" || type === "InvestorNoRatio");
					},
				},
				{
					name: "loanPurpose",
					label: "Loan Purpose",
					component: "select",
					options: LOAN_PURPOSE_TYPES,
					required: true,
				},
				{
					name: "purchasePrice",
					label: "Purchase Price",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "estimatedValue",
					label: "Estimated / Appraised Value",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: true,
				},
				{
					name: "loanAmount",
					label: "Loan Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: true,
				},
				{
					name: "closedEndSecondLienAmt",
					label: "Closed End Second Lien Amt",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "helocLineAmount",
					label: "HELOC Line Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "helocDrawnAmount",
					label: "HELOC Drawn Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
				},
				{
					name: "cashoutAmount",
					label: "Cashout Amount",
					component: "masked",
					maskType: "currency",
					placeholder: "$",
					required: (formState) => {
						const type = formState.loanPurpose;
						return type === "RefiCashout";
					},
				},
				{
					name: "ltv",
					label: "LTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (formState) => {
						const { ltv } = calculateLtvCltv({
							loanPurpose: formState.loanPurpose,
							purchasePrice: formState.purchasePrice,
							estimatedValue: formState.estimatedValue,
							loanAmount: formState.loanAmount,
							closedEndSecondLienAmt: formState.closedEndSecondLienAmt,
							helocDrawnAmount: formState.helocDrawnAmount,
							helocLineAmount: formState.helocLineAmount,
							cashoutAmount: formState.cashoutAmount,
						});
						return ltv;
					},
				},
				{
					name: "cltv",
					label: "CLTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (formState) => {
						const { cltv } = calculateLtvCltv({
							loanPurpose: formState.loanPurpose,
							purchasePrice: formState.purchasePrice,
							estimatedValue: formState.estimatedValue,
							loanAmount: formState.loanAmount,
							closedEndSecondLienAmt: formState.closedEndSecondLienAmt,
							helocDrawnAmount: formState.helocDrawnAmount,
							helocLineAmount: formState.helocLineAmount,
							cashoutAmount: formState.cashoutAmount,
						});
						return cltv;
					},
				},
				{
					name: "hcltv",
					label: "HCLTV",
					component: "masked",
					maskType: "percentage",
					placeholder: "%",
					disabled: true,
					computed: true,
					compute: (formState) => {
						const { hcltv } = calculateLtvCltv({
							loanPurpose: formState.loanPurpose,
							purchasePrice: formState.purchasePrice,
							estimatedValue: formState.estimatedValue,
							loanAmount: formState.loanAmount,
							closedEndSecondLienAmt: formState.closedEndSecondLienAmt,
							helocDrawnAmount: formState.helocDrawnAmount,
							helocLineAmount: formState.helocLineAmount,
							cashoutAmount: formState.cashoutAmount,
						});
						return hcltv;
					},
				},

				{
					name: "interestOnly",
					label: "Interest Only",
					component: "select",
					options: YES_NO,
					defaultValue: "No",
				},
				{
				name: "prePaymentPenalty",
				label: "Prepayment Penalty",
				component: "select",

				options: () => PREPAYMENT_PENALTY_OPTIONAL,

				required: (formState) => {
					const type = formState.incomeVerificationType;
					const occupancytype = formState.occupancy;

					return (
					type === "InvestorDscr" ||
					type === "InvestorNoRatio" ||
					occupancytype === "InvestmentProperty"
					);
				}
				},
				{
					name: "escrowWaiver",
					label: "Escrow Waiver",
					component: "select",
					options: ESCROW_WAIVER,
					required: true,
				},
			],
		},

		{
			name: "borrowerInformation",
			title: "Borrower Information",
			fields: [
				{
					name: "citizenship",
					label: "Citizenship",
					component: "select",
					options: CITIZENSHIP,
					required: true,
				},
				{
					name: "firstTimeHomeBuyer",
					label: "First Time Homebuyer",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "nonoccupanctCoborrower",
					label: "Non-Occupanct Coborrower",
					component: "select",
					options: YES_NO,
					defaultValue: "No",
				},
				{
					name: "selfEmployed",
					label: "Self Employed",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "reserve",
					label: "Reserve (Months)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					required: true,
				},
				{
					name: "financedProperties",
					label: "Financed Properties",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					required: true,
				},
			],
		},

		{
			name: "creditProfile",
			title: "Credit Profile",
			fields: [
				{
					name: "qualifyingCreditScore",
					label: "Qualifying Credit Score (FICO)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					required: true,
					isFullWidth: true,
				},
				{
					name: "housingEventType",
					label: "Housing Event Type",
					component: "select",
					options: HOUSING_EVENT_TYPE,
					required: true,
				},
				{
					name: "housingEventSeasoning",
					label: "Housing Event Seasoning",
					component: "select",
					options: HOUSE_EVENT_SEASONING,
					required: true,
				},
				{
					name: "bankruptcyType",
					label: "Bankruptcy Type",
					component: "select",
					options: BANKRUPTCY_TYPE,
					required: true,
				},
				{
					name: "bankruptcyOutcome",
					label: "Bankruptcy Outcome",
					component: "select",
					options: BANKRUPTCY_OUTCOME,
					required: true,
				},
				{
					name: "bankruptcySeasoning",
					label: "Bankruptcy Seasoning",
					component: "select",
					options: BANKRUPTCY_SEASONING,
					required: true,
				},
				{
					name: "mortgageLatePayments",
					label: "Mortgage Late Payments?",
					component: "select",
					options: YES_NO,
					required: true,
					defaultValue: "No",
				},
				{
					name: "descriptionOfMortgage",
					descriptionSection:
						"Enter the number of mortgage payment deliquencies within each",
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x30_12",
					label: "Mtg Lates x30 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x30_13_24",
					label: "Mtg Lates x30 (13-24 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x60_12",
					label: "Mtg Lates x60 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x60_13_24",
					label: "Mtg Lates x60 (13-24 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x90_12",
					label: "Mtg Lates x90 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x90_13_24",
					label: "Mtg Lates x90 (13-24 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x120_12",
					label: "Mtg Lates x120 (12 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
				{
					name: "Mtg_Lates_x120_13_24",
					label: "Mtg Lates x120 (13-24 mos.)",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
					visibleIf: (formState) => formState.mortgageLatePayments === "Yes",
				},
			],
		},
		{
			name: "propertyInformation",
			title: "Property Information",
			fields: [
				{
					name: "propertyType",
					label: "Property Type",
					component: "select",
					options: PROPERTY_TYPE,
					required: true,
				},
				{
					name: "occupancy",
					label: "Occupancy",
					component: "select",
					options: OCCUPANCY,
					required: true,
					computed: true,
					compute: (formState) => {
						const type = formState.incomeVerificationType;

						const isInvestor =
							type === "InvestorDscr" || type === "InvestorNoRatio";

						//  Set only if empty (first time)
						if (isInvestor && !formState.occupancy) {
							return "InvestmentProperty";
						}

						//  Otherwise keep user selection
						return formState.occupancy;
				}
				},
				{
					name: "shortTermRental",
					label: "Short Term Rental ",
					component: "select",
					options: YES_NO,
					required: true,
				},
				{
					name: "numberofUnits",
					label: "Number of Units",
					component: "select",
					options: PROPERTY_UNITS,
					required: true,
				},
				{
					name: "numberofStories",
					label: "Number of Stories ",
					component: "numeric",
					placeholder: "0",
					disabled: false,
					computed: false,
				},
				{
					name: "state",
					label: "State",
					component: "input",
					type: "text",
					placeholder: "",
					disabled: true,
				},
				{
					name: "county",
					label: "County",
					component: "input",
					type: "text",
					placeholder: "",
					disabled: true,
				},
				{
					name: "city",
					label: "City",
					component: "input",
					type: "text",
					placeholder: "",
					disabled: true,
				},
				{
					name: "zipcode",
					label: "Zipcode",
					component: "input",
					type: "text",
					placeholder: "",
					required: true,
					maxLength: 5,
				},
			],
		},
		,
		{
			name: "productPricingSearchFilters",
			title: "Product & Pricing Search Filters",
			fields: [
				{
					name: "loanTerms",
					label: "Loan Terms (Years)",
					component: "autocomplete-multi",
					options: TERMS,
					placeholder: "Search here...",
					required: true,
				},
				{
					name: "amortizationTypes",
					label: "Amortization Types",
					component: "autocomplete-multi",
					options: AMORTIZATION_TYPES,
					placeholder: "Search here...",
					required: true,
				},
				{
					name: "ARMFixedTerms",
					label: "ARM Fixed Terms",
					component: "autocomplete-multi",
					options: ARM_FIXED_TERMS,
					placeholder: "Search here...",
					required: (formState) => {
						const type = formState.amortizationTypes;
						return type.includes("ARM");
					},
				},
				{
					name: "desiredRate",
					label: "Desired Rate",
					component: "masked",
					maskType: "percentage",
					placeholder: "0.000",
				},
				{
					name: "desiredPrice",
					label: "Desired Price",
					component: "masked",
					maskType: "ratio",
					placeholder: "0",
					disabled: false,
					computed: false,
					required: false,
					defaultValue: 100.0,
				},
				{
					name: "desiredLockPeriod",
					label: "Desired Lock Period",
					component: "select",
					options: DESIRED_LOCK_PERIOD,
					required: false,
					defaultValue: "30",
				},
				{
					name: "lenderPaidvsBorrowerPaid",
					label: "Lender Paid (LPC) vs. Borrower Paid (BPC)",
					component: "select",
					options: LENDER_VS_BORROW,
					required: true,
				},
				{
					name: "productTypes",
					label: "Product Types",
					component: "autocomplete-multi",
					options: PRODUCT_TYPES,
					placeholder: "Search here...",
					disabled: true,
					defaultValue: ["StandardProducts", "ExpandedGuidelines"],
				},
				{
					name: "loanType",
					label: "Loan Type",
					component: "autocomplete-multi",
					options: LOAN_TYPES,
					placeholder: "Search here...",
					disabled: true,
					defaultValue: ["Conforming", "NonConforming", "Conventional"],
				},
				{
					name: "lienType",
					label: "Lien Type",
					component: "select",
					options: LIEN_TYPES,
					disabled: true,
					defaultValue: "First",
				},
			],
		},
	],
};
